import {
  Descriptions,
  FieldHelperText,
  Form,
  Header,
  Label,
  Space,
  StatusBadge,
  type StatusBadgeStatus,
} from '@procurify/ui'
import { type FormikProps, FormikProvider, useFormik } from 'formik'
import * as Yup from 'yup'

import { type RowCellMenuListItemProps } from '@webapp/components/DataTable'
import { UserAvatar } from '@webapp/components/UserAvatar'
import { useFormatDate } from '@webapp/core/components/FormattedDate/useFormatDate'
import { useProcurifyIntl } from '@webapp/hooks'
import {
  AttachableTravelTicketSelect,
  type AttachableTravelTicketSelectValue,
} from '@webapp/modules/expense/components/AttachableTravelTicketSelect'
import { ExpenseItemTable } from '@webapp/modules/expense/components/ExpenseItemTable'
import {
  NextApproverSelect,
  type NextApproverSelectValue,
} from '@webapp/modules/expense/components/NextApproverSelect'
import {
  RequestablePrimaryCategorySelect,
  type RequestablePrimaryCategorySelectValue,
} from '@webapp/modules/expense/components/RequestablePrimaryCategorySelect'
import {
  RequestableSecondaryCategorySelect,
  type RequestableSecondaryCategorySelectValue,
} from '@webapp/modules/expense/components/RequestableSecondaryCategorySelect'
import { type Expense } from '@webapp/modules/expense/entities/Expense'
import { type ExpenseItem } from '@webapp/modules/expense/entities/ExpenseItem'
import messages from './messages'

export interface ExpenseReportFormSchema {
  title: string
  location: RequestablePrimaryCategorySelectValue | null
  department: RequestableSecondaryCategorySelectValue | null
  travel: AttachableTravelTicketSelectValue | null
  nextApprover: NextApproverSelectValue | null
  notes: string
}

export interface IExpenseReportFormProps {
  expense: Expense
  initialValues?: Partial<ExpenseReportFormSchema>
  canEditLocation: boolean
  canEditDepartment: boolean
  canEditTravelTicket: boolean
  canEditNextApprover: boolean
  statusBadges: { type: StatusBadgeStatus; text: string }[]
  isSubmitting: boolean
  formRef?: React.MutableRefObject<FormikProps<ExpenseReportFormSchema> | null>
  onSubmit: (values: ExpenseReportFormSchema) => void
  onEditItem: (item: ExpenseItem) => void
  onLocationChange?: (locationId: number) => void
  onDepartmentChange?: (departmentId: number) => void
  selectedLocationId?: Nullable<number>
  selectedDepartmentId?: Nullable<number>
}

export const ExpenseReportForm = ({
  expense,
  initialValues: initialValuesOverride,
  canEditLocation,
  canEditDepartment,
  canEditTravelTicket,
  canEditNextApprover,
  statusBadges,
  isSubmitting,
  formRef,
  onSubmit,
  onEditItem,
  onLocationChange,
  onDepartmentChange,
  selectedLocationId,
  selectedDepartmentId,
}: IExpenseReportFormProps) => {
  const intl = useProcurifyIntl()
  const formatDate = useFormatDate()

  const accountCodeIds = expense.items.map(
    (item) => item.account.accountCode.id
  )
  const amount = expense.totalAmount

  const validationSchema = Yup.object({
    title: Yup.string()
      .required(intl.formatMessage(messages.titleRequired))
      .trim(),
    location: Yup.mixed().nullable(),
    department: Yup.mixed().nullable(),
    travel: Yup.mixed().nullable(),
    nextApprover: canEditNextApprover
      ? Yup.mixed()
          .nullable()
          .test(
            'nextApprover-required',
            intl.formatMessage(messages.nextApproverRequired),
            (value) => value !== null && value !== undefined
          )
      : Yup.mixed().nullable(),
    notes: Yup.string()
      .nullable()
      .max(2000, intl.formatMessage(messages.purposeMaxLength)),
  })

  const resolvedInitialValues: ExpenseReportFormSchema = {
    title: initialValuesOverride?.title ?? expense.title,
    location:
      initialValuesOverride?.location !== undefined
        ? initialValuesOverride.location
        : {
            id: expense.primaryCategory.id,
            name: expense.primaryCategory.name,
          },
    department:
      initialValuesOverride?.department !== undefined
        ? initialValuesOverride.department
        : {
            id: expense.secondaryCategory.id,
            name: expense.secondaryCategory.name,
          },
    travel:
      initialValuesOverride?.travel !== undefined
        ? initialValuesOverride.travel
        : expense.travel?.id != null
          ? {
              id: expense.travel.id,
              displayLabel: intl.formatMessage(messages.travelTicketValue, {
                id: expense.travel.id,
                purpose: expense.travel.purpose,
              }),
            }
          : null,
    nextApprover:
      initialValuesOverride?.nextApprover !== undefined
        ? initialValuesOverride.nextApprover
        : null,
    notes: initialValuesOverride?.notes ?? expense.description,
  }

  const formik = useFormik<ExpenseReportFormSchema>({
    initialValues: resolvedInitialValues,
    validationSchema,
    onSubmit,
    enableReinitialize: false,
  })

  if (formRef) {
    formRef.current = formik
  }

  const getMenuListItems = (
    item: ExpenseItem,
    _rowIndex: number
  ): RowCellMenuListItemProps[] => [
    {
      text: intl.formatMessage(messages.editExpenseLineMenuLabel),
      onClick: () => onEditItem(item),
    },
  ]

  const effectiveLocationId =
    selectedLocationId !== undefined
      ? selectedLocationId
      : expense.primaryCategory.id
  const effectiveDepartmentId =
    selectedDepartmentId !== undefined
      ? selectedDepartmentId
      : expense.secondaryCategory.id

  return (
    <FormikProvider value={formik}>
      <Form.Content noValidate autoComplete='off'>
        <Space size='large'>
          <Space>
            <Space direction='horizontal' align='center'>
              <Header as='h2'>
                {intl.formatMessage(messages.expenseReportHeading, {
                  id: expense.id,
                })}
              </Header>
              <Space.Item minWidth={300}>
                <Form.TextField
                  id='title'
                  name='title'
                  fullWidth
                  placeholder={intl.formatMessage(messages.titlePlaceholder)}
                  required
                  disabled={isSubmitting}
                />
              </Space.Item>
              {statusBadges.map((badge, index) => (
                <StatusBadge key={index} type={badge.type} text={badge.text} />
              ))}
            </Space>

            <Space direction='horizontal'>
              <Space.Item width={1 / 2}>
                <Descriptions columns={1}>
                  <Descriptions.Item
                    label={intl.formatMessage(messages.requestedByLabel)}
                  >
                    <Label
                      component={
                        <UserAvatar
                          shadow={false}
                          url={expense.requester.profileImage ?? undefined}
                          size='xsmall'
                          circular
                        />
                      }
                    >
                      {expense.requester.fullName}
                    </Label>
                  </Descriptions.Item>

                  {canEditLocation ? (
                    <Descriptions.Item
                      label={intl.formatMessage(messages.locationLabel)}
                    >
                      <RequestablePrimaryCategorySelect
                        value={formik.values.location}
                        onChange={(option) => {
                          formik.setFieldValue('location', option)
                          formik.setFieldValue('department', null)
                          formik.setFieldValue('nextApprover', null)
                          if (option && onLocationChange) {
                            onLocationChange(option.id)
                          }
                        }}
                        isDisabled={isSubmitting}
                      />
                    </Descriptions.Item>
                  ) : (
                    <Descriptions.Item
                      label={intl.formatMessage(messages.locationLabel)}
                    >
                      {expense.primaryCategory.name}
                    </Descriptions.Item>
                  )}

                  {canEditDepartment ? (
                    <Descriptions.Item
                      label={intl.formatMessage(messages.departmentLabel)}
                    >
                      <RequestableSecondaryCategorySelect
                        locationId={effectiveLocationId}
                        value={formik.values.department}
                        onChange={(option) => {
                          formik.setFieldValue('department', option)
                          formik.setFieldValue('nextApprover', null)
                          if (option && onDepartmentChange) {
                            onDepartmentChange(option.id)
                          }
                        }}
                        isDisabled={
                          isSubmitting || effectiveLocationId === null
                        }
                      />
                    </Descriptions.Item>
                  ) : (
                    <Descriptions.Item
                      label={intl.formatMessage(messages.departmentLabel)}
                    >
                      {expense.secondaryCategory.name}
                    </Descriptions.Item>
                  )}

                  {canEditTravelTicket && (
                    <Descriptions.Item
                      label={intl.formatMessage(messages.travelTicketLabel)}
                    >
                      <AttachableTravelTicketSelect
                        userId={expense.requester.id}
                        value={formik.values.travel}
                        onChange={(option) =>
                          formik.setFieldValue('travel', option)
                        }
                        isClearable
                        isDisabled={isSubmitting}
                      />
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </Space.Item>

              <Space.Item width={1 / 2}>
                <Descriptions columns={1}>
                  {canEditNextApprover && (
                    <Descriptions.Item
                      label={intl.formatMessage(messages.nextApproverLabel)}
                    >
                      <Space direction='horizontal' align='center'>
                        <NextApproverSelect
                          departmentId={effectiveDepartmentId}
                          accountCodeIds={accountCodeIds}
                          amount={amount}
                          value={formik.values.nextApprover}
                          onChange={(option) =>
                            formik.setFieldValue('nextApprover', option)
                          }
                          isDisabled={
                            isSubmitting || effectiveDepartmentId === null
                          }
                          error={Boolean(
                            formik.touched.nextApprover &&
                            formik.errors.nextApprover
                          )}
                        />
                        {formik.touched.nextApprover &&
                          formik.errors.nextApprover && (
                            <FieldHelperText error>
                              {String(formik.errors.nextApprover)}
                            </FieldHelperText>
                          )}
                      </Space>
                    </Descriptions.Item>
                  )}

                  <Descriptions.Item
                    label={intl.formatMessage(messages.submittedLabel)}
                  >
                    {formatDate(expense.submittedDate, {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label={intl.formatMessage(messages.lastModifiedLabel)}
                  >
                    {formatDate(expense.dateLastModified, {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </Descriptions.Item>
                </Descriptions>
              </Space.Item>
            </Space>
          </Space>

          <Space>
            <Header as='h4'>
              {intl.formatMessage(messages.expensePurposeHeading)}
            </Header>
            <Form.TextField
              id='notes'
              name='notes'
              fullWidth
              multiline
              minRows={4}
              placeholder={intl.formatMessage(messages.purposePlaceholder)}
              inputProps={{ maxLength: 2000 }}
              disabled={isSubmitting}
            />
          </Space>

          <ExpenseItemTable
            items={expense.items}
            menuListItems={getMenuListItems}
          />
        </Space>
      </Form.Content>
    </FormikProvider>
  )
}
